/* PrivateCourt Sales Outreach — multi-tab Admin panel.
 *
 *   Tab 1: LEAD POOL          — non-DNC institutions with on-file emails.
 *                                Multi-select → bulk AI-draft.
 *   Tab 2: APPROVAL QUEUE     — drafts (incl. system-generated follow-ups).
 *                                Recipient is a dropdown of on-file contacts.
 *   Tab 3: SENT & ENGAGEMENT  — opens/clicks/bounces from Resend webhook.
 *                                Mark-replied to cancel follow-ups.
 *   Tab 4: FOLLOW-UPS         — pending Day+4 / Day+9 schedule.
 *   Tab 5: SETTINGS           — Resend key + webhook URL + sender config.
 *
 * Every draft (initial OR follow-up) is approval-gated. DNC firewall enforced
 * at draft creation AND at send time.
 * ------------------------------------------------------------------------ */
import { useCallback, useEffect, useMemo, useState } from "react";
import { httpAuth as http } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Search,
  Sparkles,
  Send,
  Check,
  X as XIcon,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Mail,
  ListChecks,
  Inbox,
  Wand2,
  Users,
  Clock,
  Settings as SettingsIcon,
  CheckCircle2,
  XCircle,
  MailOpen,
  MousePointerClick,
  ChevronLeft,
  ChevronRight,
  Play,
  Square,
  Activity,
} from "lucide-react";

const PILL = "border border-[#0A0A0A] px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em]";
const ROLE_LABEL = { recovery: "Recovery Head", legal: "Legal Head" };

const BrutalBtn = ({ children, testid, variant = "dark", className = "", ...rest }) => {
  const base =
    "inline-flex items-center justify-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    dark: "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#002FA7] hover:border-[#002FA7]",
    ghost: "bg-white text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white",
    danger: "bg-white text-[#FF3333] border-[#FF3333] hover:bg-[#FF3333] hover:text-white",
    success: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700",
  };
  return (
    <button data-testid={testid} className={`${base} ${variants[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
};

/* ============================================================================
 * TAB 1: LEAD POOL — pick whom to draft for
 * ========================================================================= */
const LeadPoolTab = ({ onBulkDrafted }) => {
  const [data, setData] = useState({ items: [], total_eligible_in_window: 0, scanned: 0, skipped_dnc: 0, skipped_no_email: 0 });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [type_, setType] = useState("All");
  const [role, setRole] = useState("recovery");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selected, setSelected] = useState({}); // slug → true
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ role, page: String(page), page_size: String(pageSize) });
      if (q.trim()) params.append("q", q.trim());
      if (type_ !== "All") params.append("type", type_);
      const { data } = await http.get(`/admin/sales/leads?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Lead pool load failed"));
    } finally {
      setBusy(false);
    }
  }, [q, type_, role, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAll = () => {
    if (Object.keys(selected).length === data.items.length) setSelected({});
    else setSelected(Object.fromEntries(data.items.map((it) => [it.slug, true])));
  };

  const selectedSlugs = Object.keys(selected).filter((k) => selected[k]);

  const runBulkDraft = async () => {
    if (selectedSlugs.length === 0) return toast.error("Select at least one institution");
    if (selectedSlugs.length > 25) return toast.error("Max 25 per bulk draft");
    if (!window.confirm(`AI-draft emails for ${selectedSlugs.length} institutions?\n\nThis will take ~${Math.ceil(selectedSlugs.length * 25 / 60)} minute(s). Each draft will still need your approval before send.`)) return;
    setDrafting(true);
    const t = toast.loading(`Drafting ${selectedSlugs.length} emails…`);
    try {
      const { data } = await http.post("/admin/sales/bulk-draft", {
        slugs: selectedSlugs,
        role,
        auto_pick: true,
      });
      toast.success(`Created ${data.created} drafts · skipped ${data.skipped}`, { id: t, duration: 6000 });
      setSelected({});
      onBulkDrafted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Bulk draft failed"), { id: t });
    } finally {
      setDrafting(false);
    }
  };

  const runHarvest = async () => {
    const filterDesc = `${type_ !== "All" ? `type=${type_}` : "all types"}${q ? `, q="${q}"` : ""}`;
    if (!window.confirm(`Harvest emails for ${filterDesc}?\n\nFor each institution we crawl its website + key sub-pages AND run a web-search pass (Brave + DDG + Bing) for emails on RBI, news, PDFs and third-party listings. Filters junk (noreply, etc.). Takes ~1-2 min per 50 institutions.`)) return;
    setDrafting(true);
    const t = toast.loading("Harvesting emails (websites + web search)…");
    try {
      const { data } = await http.post("/admin/sales/harvest", {
        type: type_ === "All" ? null : type_,
        q: q || null,
        limit: 100,
        only_missing_emails: false,
      });
      toast.success(
        `Scanned ${data.scanned} · ${data.new_emails_total} new emails across ${data.with_new_emails} institutions`,
        { id: t, duration: 8000 },
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Harvest failed"), { id: t });
    } finally {
      setDrafting(false);
    }
  };

  const runWebSearch = async () => {
    const filterDesc = `${type_ !== "All" ? `type=${type_}` : "all types"}${q ? `, q="${q}"` : ""}`;
    if (!window.confirm(`Web-search-only sweep for ${filterDesc}?\n\nSkips on-site crawling — runs Brave + DDG + Bing only. Best for institutions already harvested once where you want to find more emails on RBI, news sites and PDF circulars.\n\nLimit: 50 institutions per run.`)) return;
    setDrafting(true);
    const t = toast.loading("Searching the web for more emails…");
    try {
      const { data } = await http.post("/admin/sales/web-search", {
        type: type_ === "All" ? null : type_,
        q: q || null,
        limit: 50,
      });
      toast.success(
        `Scanned ${data.scanned} · ${data.new_emails_total} new emails across ${data.with_new_emails} institutions`,
        { id: t, duration: 8000 },
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Web search failed"), { id: t });
    } finally {
      setDrafting(false);
    }
  };

  const runAutoPipeline = async () => {
    if (selectedSlugs.length === 0) return toast.error("Select at least one institution");
    if (selectedSlugs.length > 25) return toast.error("Max 25 per run");
    const eta = Math.ceil(selectedSlugs.length * 30 / 60);
    if (!window.confirm(`Auto-pipeline for ${selectedSlugs.length} institutions?\n\nFor each: DNC check → harvest more emails (if <2 on file) → AI research → AI draft → save to Approval Queue.\n\nEstimated time: ~${eta} minute(s). Nothing sends without your approval.`)) return;
    setDrafting(true);
    const t = toast.loading(`Auto-pipeline running on ${selectedSlugs.length}…`);
    try {
      const { data } = await http.post("/admin/sales/auto-pipeline", {
        slugs: selectedSlugs,
        role,
        skip_if_drafted: true,
        force_harvest: false,
      });
      toast.success(
        `Drafted ${data.drafted} · harvested ${data.harvested_emails_total} new emails · skipped ${data.skipped_dnc} DNC + ${data.skipped_drafted} already-drafted`,
        { id: t, duration: 8000 },
      );
      setSelected({});
      onBulkDrafted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Auto-pipeline failed"), { id: t });
    } finally {
      setDrafting(false);
    }
  };


  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-[#0A0A0A] p-4 bg-white">
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search by name…"
          data-testid="leads-q"
          className="border border-[#0A0A0A] px-3 py-2 text-sm font-mono"
        />
        <select
          value={type_}
          onChange={(e) => { setType(e.target.value); setPage(1); }}
          data-testid="leads-type"
          className="border border-[#0A0A0A] px-2 py-2 text-sm font-mono"
        >
          <option>All</option>
          <option>Bank</option>
          <option>NBFC</option>
        </select>
        <select
          value={role}
          onChange={(e) => { setRole(e.target.value); setPage(1); }}
          data-testid="leads-role"
          className="border border-[#0A0A0A] px-2 py-2 text-sm font-mono"
        >
          <option value="recovery">Recovery Head (auto-pick)</option>
          <option value="legal">Legal Head (auto-pick)</option>
        </select>
        <button onClick={load} disabled={busy} data-testid="leads-refresh" className={`${PILL} bg-white`}>
          <RefreshCw className={`w-3 h-3 inline -mt-0.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
          {data.scanned} scanned · {data.skipped_dnc} DNC · {data.skipped_no_email} no-email ·{" "}
          <span className="font-bold text-[#0A0A0A]" data-testid="leads-eligible">{data.items.length}</span> eligible on this page ·{" "}
          <span className="font-bold text-[#002FA7]" data-testid="leads-selected">{selectedSlugs.length}</span> selected
        </div>
        <div className="flex gap-2 flex-wrap">
          <BrutalBtn testid="leads-harvest" variant="ghost" onClick={runHarvest} disabled={drafting}>
            <Search className="w-3.5 h-3.5" /> Harvest (site + web)
          </BrutalBtn>
          <BrutalBtn testid="leads-web-search" variant="ghost" onClick={runWebSearch} disabled={drafting}>
            <Search className="w-3.5 h-3.5" /> Web search only
          </BrutalBtn>
          <BrutalBtn testid="leads-auto-pipeline" variant="dark" onClick={runAutoPipeline} disabled={drafting || selectedSlugs.length === 0}>
            {drafting ? (
              <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Working…</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5" /> Harvest + Draft {selectedSlugs.length}</>
            )}
          </BrutalBtn>
          <BrutalBtn testid="leads-bulk-draft" variant="success" onClick={runBulkDraft} disabled={drafting || selectedSlugs.length === 0}>
            {drafting ? (
              <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Working…</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5" /> Draft only ({selectedSlugs.length})</>
            )}
          </BrutalBtn>
        </div>
      </div>

      <div className="border border-[#0A0A0A] bg-white max-h-[60vh] overflow-auto" data-testid="leads-list">
        <table className="w-full text-xs">
          <thead className="bg-[#0A0A0A] text-white sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  checked={data.items.length > 0 && selectedSlugs.length === data.items.length}
                  onChange={toggleAll}
                  data-testid="leads-select-all"
                />
              </th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Institution</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Type</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">State</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Auto-picked email ({ROLE_LABEL[role]})</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-10 text-[#4B5563]">No leads matched. Try a different role or remove filters.</td></tr>
            ) : data.items.map((it) => (
              <tr key={it.slug} className={`border-t border-[#0A0A0A]/10 hover:bg-[#F8F9FA] ${selected[it.slug] ? "bg-blue-50" : ""}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!selected[it.slug]}
                    onChange={() => setSelected((s) => ({ ...s, [it.slug]: !s[it.slug] }))}
                    data-testid={`leads-select-${it.slug}`}
                  />
                </td>
                <td className="px-3 py-2 font-bold">{it.name}</td>
                <td className="px-3 py-2 font-mono text-[10px] uppercase">{it.type}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{it.hq_state}</td>
                <td className="px-3 py-2 font-mono text-[#002FA7]">{it.picked?.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="leads-prev" className={`${PILL} bg-white`}>
          <ChevronLeft className="w-3 h-3 inline -mt-0.5" /> Prev
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Page {page}</span>
        <button onClick={() => setPage((p) => p + 1)} disabled={data.items.length < pageSize} data-testid="leads-next" className={`${PILL} bg-white`}>
          Next <ChevronRight className="w-3 h-3 inline -mt-0.5" />
        </button>
      </div>
    </div>
  );
};

/* ============================================================================
 * TAB 2: APPROVAL QUEUE (with recipient-dropdown + research preview)
 * ========================================================================= */
const QueueTab = ({ refreshKey }) => {
  const [data, setData] = useState({ counts: {}, items: [] });
  const [filter, setFilter] = useState("draft");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/admin/sales/drafts${filter ? `?status=${encodeURIComponent(filter)}` : ""}`);
      setData(data);
    } catch {
      setData({ counts: {}, items: [] });
    }
  }, [filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const tabs = [
    { k: "draft", label: "Draft" },
    { k: "needs_email", label: "Needs email" },
    { k: "approved", label: "Approved" },
    { k: "rejected", label: "Rejected" },
    { k: "sent", label: "Sent" },
    { k: "failed", label: "Failed" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="queue-tabs">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`queue-tab-${t.k}`}
            className={`${PILL} ${filter === t.k ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
          >
            {t.label} · {data.counts[t.k] ?? 0}
          </button>
        ))}
        <button onClick={load} data-testid="queue-refresh" className={`${PILL} bg-white`}>
          <RefreshCw className="w-3 h-3 inline -mt-0.5 mr-1" /> Refresh
        </button>
      </div>

      <div className="border border-[#0A0A0A] bg-white" data-testid="queue-list">
        {data.items.length === 0 ? (
          <div className="text-center py-10 text-[#4B5563] text-sm">
            Empty. Use the Lead Pool tab to draft new emails.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Institution</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Role / Stage</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Recipient</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Subject</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id} className="border-t border-[#0A0A0A]/15 hover:bg-[#F8F9FA]">
                  <td className="px-3 py-2 font-bold">
                    {d.name}
                    {d.is_followup && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-200 text-amber-900 font-mono text-[9px] uppercase tracking-wider">
                        Follow-up {d.followup_stage}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider">
                    {ROLE_LABEL[d.role] || d.role}
                  </td>
                  <td className="px-3 py-2 font-mono">{d.recipient_email || <span className="text-[#FF3333]">—</span>}</td>
                  <td className="px-3 py-2 max-w-[300px] truncate">{d.subject}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#4B5563]">
                    {d.updated_at ? new Date(d.updated_at).toLocaleString("en-IN") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setOpenId(d.id)} data-testid={`queue-open-${d.id}`} className="font-mono text-[10px] uppercase tracking-wider underline">Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && <DraftModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
};

/* ============================================================================
 * DraftModal (re-used by Queue tab)
 * ========================================================================= */
const DraftModal = ({ id, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("draft"); // draft | preview | research

  const toggleRecipient = (email) => {
    setSelectedRecipients((s) => {
      const n = new Set(s);
      if (n.has(email)) n.delete(email);
      else n.add(email);
      return n;
    });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/admin/sales/drafts/${id}`);
      setDoc(data);
      setSubject(data.subject || "");
      setBody(data.body_md || "");
      // Load contacts for the fan-out preview (best-effort)
      if (data.slug) {
        try {
          const { data: cc } = await http.get(`/admin/sales/contacts/${data.slug}`);
          setContacts(cc.contacts || []);
          // Default: ALL on-file recipients selected (the user's explicit ask).
          const allEmails = new Set((cc.contacts || []).map((c) => c.email.toLowerCase()));
          if (data.recipient_email) allEmails.add(data.recipient_email.toLowerCase());
          setSelectedRecipients(allEmails);
        } catch {
          setContacts([]);
          if (data.recipient_email) setSelectedRecipients(new Set([data.recipient_email.toLowerCase()]));
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load draft"));
      onClose();
    }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/admin/sales/drafts/${id}`, { subject, body_md: body });
      setDoc(data);
      setEditing(false);
      toast.success("Saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Save failed"));
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    const note = window.prompt("Optional steering note for the AI (e.g. 'shorter, 300 words')", "");
    if (note === null) return;
    setBusy(true);
    const t = toast.loading("AI regenerating…");
    try {
      const { data } = await http.post(`/admin/sales/drafts/${id}/regenerate`, { note });
      setDoc(data);
      setSubject(data.subject || "");
      setBody(data.body_md || "");
      toast.success("Regenerated", { id: t });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Regenerate failed"), { id: t });
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (selectedRecipients.size === 0) {
      toast.error("Select at least one recipient");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/admin/sales/drafts/${id}/approve`);
      toast.success(`Approved — fans out to ${selectedRecipients.size} recipient${selectedRecipients.size === 1 ? "" : "s"}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Approve failed")); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!window.confirm("Reject this draft?")) return;
    setBusy(true);
    try { await http.post(`/admin/sales/drafts/${id}/reject`); toast.success("Rejected"); onChanged?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Reject failed")); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const recipientArr = Array.from(selectedRecipients);
    if (recipientArr.length === 0) return toast.error("No recipients selected");
    const eta = Math.ceil(recipientArr.length * 1.2);
    if (!window.confirm(`Send to ${recipientArr.length} address${recipientArr.length === 1 ? "" : "es"} individually?\n\nEstimated time: ~${eta} seconds. Each gets its own tracked send.`)) return;
    setBusy(true);
    const t = toast.loading(`Fanning out to ${recipientArr.length}…`);
    try {
      const { data } = await http.post(`/admin/sales/drafts/${id}/send`, { recipients: recipientArr });
      toast.success(`Sent ${data.fanout_count} · ${data.failures?.length || 0} failures`, { id: t, duration: 6000 });
      onChanged?.();
      onClose();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Send failed"), { id: t, duration: 8000 }); }
    finally { setBusy(false); }
  };

  const removeDraft = async () => {
    if (!window.confirm("Delete this draft permanently?")) return;
    setBusy(true);
    try { await http.delete(`/admin/sales/drafts/${id}`); toast.success("Deleted"); onChanged?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Delete failed")); }
    finally { setBusy(false); }
  };

  if (!doc) return <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="queue-modal-loading"><div className="bg-white px-6 py-4 border border-[#0A0A0A] font-mono text-xs uppercase tracking-wider">Loading…</div></div>;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="queue-modal">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col border border-[#0A0A0A]">
        <div className="flex items-center justify-between border-b border-[#0A0A0A] px-5 py-3 bg-[#F8F9FA]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
              Draft · {doc.status}{doc.is_followup ? ` · Follow-up ${doc.followup_stage}` : ""}
            </div>
            <div className="font-display font-black text-lg">{doc.name}</div>
            <div className="font-mono text-[10px] text-[#4B5563]">{ROLE_LABEL[doc.role]}</div>
          </div>
          <button onClick={onClose} data-testid="queue-modal-close" className="p-2 hover:bg-[#0A0A0A] hover:text-white"><XIcon className="w-4 h-4" /></button>
        </div>

        {/* Fan-out preview — on approve+send, fans out to ALL on-file emails.
            Admin can uncheck individual addresses to skip them. */}
        <div className="border-b border-[#0A0A0A] px-5 py-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
              Recipients (sends individually — fan-out)
            </label>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#002FA7] text-white px-2 py-1" data-testid="queue-fanout-count">
              {selectedRecipients.size} of {contacts.length || (doc.recipient_email ? 1 : 0)} selected
            </span>
          </div>
          {contacts.length === 0 ? (
            doc.recipient_email ? (
              <div className="text-xs font-mono">
                <input
                  type="checkbox"
                  checked={selectedRecipients.has(doc.recipient_email.toLowerCase())}
                  onChange={() => toggleRecipient(doc.recipient_email.toLowerCase())}
                  className="mr-2"
                /> {doc.recipient_email}
              </div>
            ) : (
              <p className="text-xs text-[#FF3333]">No on-file emails. Run &quot;Harvest emails&quot; on the Lead Pool tab.</p>
            )
          ) : (
            <div className="max-h-40 overflow-auto border border-[#0A0A0A]/20" data-testid="queue-recipient-list">
              {contacts.map((c, i) => {
                const em = c.email.toLowerCase();
                return (
                  <label key={em + i} className="flex items-center gap-2 px-2 py-1 hover:bg-[#F8F9FA] cursor-pointer border-b border-[#0A0A0A]/5">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.has(em)}
                      onChange={() => toggleRecipient(em)}
                      data-testid={`queue-recipient-${em}`}
                    />
                    <span className="font-mono text-xs flex-1">{c.email}</span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-[#4B5563]">
                      {c.role || "—"}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-[#4B5563] mt-2">
            Each address gets its own individual send. Throttled at ~50/min.
          </p>
        </div>

        <div className="flex gap-2 border-b border-[#0A0A0A] px-5 py-2 bg-white">
          {[{ k: "draft", label: "Markdown" }, { k: "preview", label: "HTML preview" }, { k: "research", label: "AI research" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`queue-modal-tab-${t.k}`} className={`${PILL} ${tab === t.k ? "bg-[#0A0A0A] text-white" : "bg-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {tab === "draft" && (
            <div className="space-y-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Subject</label>
                {editing ? (
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="queue-edit-subject" className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono" />
                ) : (
                  <div className="border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-[#F8F9FA]">{doc.subject}</div>
                )}
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Body (Markdown)</label>
                {editing ? (
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="queue-edit-body" rows={22} className="w-full border border-[#0A0A0A] p-3 text-xs font-mono" />
                ) : (
                  <pre className="border border-[#0A0A0A] p-3 text-xs font-mono whitespace-pre-wrap bg-[#F8F9FA] max-h-[55vh] overflow-auto">{doc.body_md}</pre>
                )}
              </div>
            </div>
          )}
          {tab === "preview" && <HtmlPreview id={id} />}
          {tab === "research" && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">AI research output</span>
                {doc.research?.source && (
                  <span className={`${PILL} ${doc.research.source === "llm" ? "bg-emerald-600 text-white border-emerald-600" : "bg-amber-500 text-white border-amber-500"}`} data-testid="queue-research-source">
                    {doc.research.source === "llm" ? "LLM-generated" : doc.research.source === "followup-template" ? "Follow-up template" : "Fallback template"}
                  </span>
                )}
              </div>
              <div className="border border-[#0A0A0A] p-3 bg-[#F8F9FA]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Headline pain</div>
                <div>{doc.research?.headline_pain || "—"}</div>
              </div>
              <div className="border border-[#0A0A0A] p-3 bg-[#F8F9FA]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Angle</div>
                <div>{doc.research?.angle || "—"}</div>
              </div>
              <div className="border border-[#0A0A0A] p-3 bg-[#F8F9FA]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Metrics</div>
                <ul className="list-disc pl-5">{(doc.research?.metrics || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
              <div className="border border-[#0A0A0A] p-3 bg-[#F8F9FA]">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Category-specific pains</div>
                <ul className="list-disc pl-5">{(doc.research?.category_pains || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#0A0A0A] px-5 py-3 bg-[#F8F9FA] flex flex-wrap justify-between gap-2">
          <div className="flex gap-2 flex-wrap">
            {editing ? (
              <>
                <BrutalBtn testid="queue-save" variant="dark" onClick={saveEdits} disabled={busy}><Check className="w-3.5 h-3.5" /> Save edits</BrutalBtn>
                <BrutalBtn testid="queue-cancel-edit" variant="ghost" onClick={() => { setEditing(false); setSubject(doc.subject); setBody(doc.body_md); }} disabled={busy}>Cancel</BrutalBtn>
              </>
            ) : (
              <>
                <BrutalBtn testid="queue-edit" variant="ghost" onClick={() => setEditing(true)} disabled={busy || doc.status === "sent"}>Edit</BrutalBtn>
                {!doc.is_followup && (
                  <BrutalBtn testid="queue-regenerate" variant="ghost" onClick={regenerate} disabled={busy || doc.status === "sent"}><Wand2 className="w-3.5 h-3.5" /> Regenerate</BrutalBtn>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <BrutalBtn testid="queue-delete" variant="danger" onClick={removeDraft} disabled={busy}><Trash2 className="w-3.5 h-3.5" /></BrutalBtn>
            {doc.status === "draft" && (
              <>
                <BrutalBtn testid="queue-reject" variant="danger" onClick={reject} disabled={busy}>Reject</BrutalBtn>
                <BrutalBtn testid="queue-approve" variant="success" onClick={approve} disabled={busy}><Check className="w-3.5 h-3.5" /> Approve</BrutalBtn>
              </>
            )}
            {doc.status === "approved" && (
              <BrutalBtn testid="queue-send" variant="success" onClick={send} disabled={busy}><Send className="w-3.5 h-3.5" /> Send now</BrutalBtn>
            )}
            {doc.status === "sent" && (
              <span className={`${PILL} bg-emerald-50 text-emerald-700 border-emerald-700`}>✓ Sent {doc.sent_at ? new Date(doc.sent_at).toLocaleString("en-IN") : ""}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const HtmlPreview = ({ id }) => {
  const [html, setHtml] = useState("");
  useEffect(() => { http.get(`/admin/sales/drafts/${id}/preview`).then((r) => setHtml(r.data.html || "")).catch(() => {}); }, [id]);
  if (!html) return <div className="text-sm text-[#4B5563]">Loading preview…</div>;
  return <iframe title="Email preview" srcDoc={html} className="w-full h-[60vh] border border-[#0A0A0A] bg-white" data-testid="queue-html-preview" />;
};

/* ============================================================================
 * TAB 3: SENT & ENGAGEMENT
 * ========================================================================= */
const SentTab = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const load = useCallback(async () => {
    try { const { data } = await http.get("/admin/sales/sent"); setData(data); }
    catch { setData({ total: 0, items: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markReplied = async (id) => {
    if (!id) return toast.error("No id on this record (older sent — recreate to enable)");
    try { await http.post(`/admin/sales/sent/${id}/mark-replied`); toast.success("Marked replied · follow-ups cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Mark failed")); }
  };
  const cancelFollowups = async (id) => {
    if (!id) return toast.error("No id on this record");
    try { await http.post(`/admin/sales/sent/${id}/cancel-followups`); toast.success("Follow-ups cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Cancel failed")); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]" data-testid="sent-total">
          {data.total} email{data.total === 1 ? "" : "s"} dispatched
        </span>
        <button onClick={load} data-testid="sent-refresh" className={`${PILL} bg-white`}><RefreshCw className="w-3 h-3 inline -mt-0.5 mr-1" /> Refresh</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white max-h-[60vh] overflow-auto">
        {data.items.length === 0 ? (
          <div className="text-center py-10 text-[#4B5563] text-sm">Nothing sent yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Institution</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Recipient</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Sent</th>
                <th className="text-center px-3 py-2 font-mono uppercase tracking-wider"><MailOpen className="w-3 h-3 inline" /> Opens</th>
                <th className="text-center px-3 py-2 font-mono uppercase tracking-wider"><MousePointerClick className="w-3 h-3 inline" /> Clicks</th>
                <th className="text-center px-3 py-2 font-mono uppercase tracking-wider">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id || s.resend_id || s.draft_id} className="border-t border-[#0A0A0A]/15">
                  <td className="px-3 py-2 font-bold">{s.name}{s.is_followup && <span className="ml-2 px-1.5 py-0.5 bg-amber-200 text-amber-900 font-mono text-[9px] uppercase tracking-wider">F/U</span>}</td>
                  <td className="px-3 py-2 font-mono">{s.recipient_email}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#4B5563]">{s.sent_at ? new Date(s.sent_at).toLocaleString("en-IN") : ""}</td>
                  <td className="px-3 py-2 text-center font-bold">{s.opens || 0}</td>
                  <td className="px-3 py-2 text-center font-bold">{s.clicks || 0}</td>
                  <td className="px-3 py-2 text-center">
                    {s.replied ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Replied</span>
                      : s.bounced ? <span className="inline-flex items-center gap-1 text-[#FF3333]"><XCircle className="w-3 h-3" /> Bounced</span>
                      : s.complained ? <span className="inline-flex items-center gap-1 text-amber-700">Complaint</span>
                      : s.delivered ? <span className="text-[#4B5563]">Delivered</span>
                      : <span className="text-[#4B5563]">Sent</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!s.replied && (
                      <button onClick={() => markReplied(s.id)} data-testid={`sent-mark-replied-${s.id}`} className="font-mono text-[10px] uppercase tracking-wider underline mr-2">Mark replied</button>
                    )}
                    {(s.next_followup_at || s.final_followup_at) && (
                      <button onClick={() => cancelFollowups(s.id)} data-testid={`sent-cancel-fu-${s.id}`} className="font-mono text-[10px] uppercase tracking-wider underline text-[#FF3333]">Cancel f/u</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ============================================================================
 * TAB 4: FOLLOW-UPS — upcoming Day+4 / Day+9
 * ========================================================================= */
const FollowupsTab = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await http.get("/admin/sales/followups/upcoming"); setData(data); }
    catch { setData({ total: 0, items: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    const t = toast.loading("Materialising due follow-ups…");
    try {
      const { data } = await http.post("/admin/sales/followups/run-now");
      toast.success(`Day+4 created: ${data.day4_created} · Day+9: ${data.day9_created} · skipped existing: ${data.skipped_existing}`, { id: t, duration: 6000 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Run failed"), { id: t });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
          {data.total} sent email{data.total === 1 ? "" : "s"} with pending follow-ups
        </span>
        <div className="flex gap-2">
          <BrutalBtn testid="followups-run-now" variant="ghost" onClick={runNow} disabled={busy}>
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Run scheduler now
          </BrutalBtn>
          <button onClick={load} data-testid="followups-refresh" className={`${PILL} bg-white`}><RefreshCw className="w-3 h-3 inline -mt-0.5 mr-1" /> Refresh</button>
        </div>
      </div>
      <p className="text-xs text-[#4B5563]">
        Day+4 nudges and Day+9 breakups are auto-materialised as DRAFTS in the Approval Queue every hour at :15. You still approve before send. Mark-replied on the Sent tab cancels both follow-ups instantly.
      </p>
      <div className="border border-[#0A0A0A] bg-white max-h-[60vh] overflow-auto">
        {data.items.length === 0 ? (
          <div className="text-center py-10 text-[#4B5563] text-sm">No follow-ups pending.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Institution</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Recipient</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Sent</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Day +4</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Day +9</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id || s.draft_id} className="border-t border-[#0A0A0A]/15">
                  <td className="px-3 py-2 font-bold">{s.name}</td>
                  <td className="px-3 py-2 font-mono">{s.recipient_email}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{s.sent_at ? new Date(s.sent_at).toLocaleString("en-IN") : ""}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{s.next_followup_at ? new Date(s.next_followup_at).toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{s.final_followup_at ? new Date(s.final_followup_at).toLocaleString("en-IN") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ============================================================================
 * TAB 5: AUTOMATION — one-button Harvest → Draft → Send across all institutions
 * ========================================================================= */
const AutomationTab = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  // Filter selectors
  const [meta, setMeta] = useState({ types: [], categories: [] });
  const [type_, setType] = useState("");          // "" = all
  const [category, setCategory] = useState("");   // "" = all categories
  const [preview, setPreview] = useState(null);   // { matches }
  // Email mode
  const [emailMode, setEmailMode] = useState("ai"); // "ai" | "fixed"
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/sales/automation/status");
      setState(data);
    } catch (e) {
      // silent — keep last known state
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  // Load /meta once for the type + category dropdowns.
  useEffect(() => {
    http.get("/meta").then((r) => setMeta(r.data || { types: [], categories: [] })).catch(() => {});
  }, []);

  // Load templates list (used when emailMode === "fixed").
  useEffect(() => {
    http.get("/admin/sales/templates")
      .then((r) => setTemplates((r.data?.items || []).filter((t) => t.is_active)))
      .catch(() => setTemplates([]));
  }, []);

  // Whenever the filter changes, fetch a fresh match count preview.
  useEffect(() => {
    const params = new URLSearchParams();
    if (type_) params.set("type", type_);
    if (category) params.set("category", category);
    http
      .get(`/admin/sales/automation/preview?${params.toString()}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [type_, category]);

  // Categories list is global; for now we let admins pick any. When type=Bank,
  // BFSI categories are irrelevant — surface a hint instead of crashing.
  const nbfcCategories = useMemo(
    () => (meta.categories || []).filter((c) => c && c.toUpperCase() !== "BANK"),
    [meta.categories],
  );

  const filterLabel = () => {
    if (!type_ && !category) return "ALL institutions";
    const parts = [];
    if (type_) parts.push(type_);
    if (category) parts.push(category);
    return parts.join(" / ");
  };

  const handleStart = async () => {
    if (emailMode === "fixed" && !templateId) {
      toast.error("Please pick a template before starting");
      return;
    }
    const scopeMsg = preview?.matches != null
      ? `Scope: ${filterLabel()} — ${preview.matches.toLocaleString()} institutions match.`
      : `Scope: ${filterLabel()}.`;
    const tpl = templates.find((t) => t.id === templateId);
    const modeMsg = emailMode === "fixed"
      ? `Email mode: FIXED TEMPLATE — "${tpl?.name || templateId}". No LLM calls.`
      : "Email mode: AI-generated (research + write_pitch per institution).";
    if (!window.confirm(
      "Start AUTOMATED outreach?\n\n" +
      scopeMsg + "\n" + modeMsg + "\n\n" +
      "For each institution (alphabetical):\n" +
      "  1. Harvest emails (site + Brave/DDG/Bing web search)\n" +
      (emailMode === "fixed"
        ? "  2. Render fixed template (no LLM)\n"
        : "  2. AI research + AI draft\n") +
      "  3. AUTO-APPROVE + fan-out send via MSG91 (Bouncify-verified)\n\n" +
      "There is NO human approval gate. Sending begins immediately.\n" +
      "You can stop gracefully at any time."
    )) return;
    setBusy(true);
    const t = toast.loading("Starting automation…");
    try {
      await http.post("/admin/sales/automation/start", {
        role: "recovery",
        type: type_ || null,
        category: category || null,
        template_id: emailMode === "fixed" ? templateId : null,
      });
      toast.success("Automation started", { id: t });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Start failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    if (!window.confirm("Request graceful stop?\n\nCurrent institution will finish, then automation halts before picking the next.")) return;
    setBusy(true);
    const t = toast.loading("Requesting stop…");
    try {
      await http.post("/admin/sales/automation/stop");
      toast.success("Stop requested — finishing current institution…", { id: t });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Stop failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  // Test mode banner — show a loud red warning whenever sends are being
  // redirected so admins never confuse a test run with a real one.
  const [testMode, setTestMode] = useState(null);
  const reloadCfg = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/sales/config");
      setTestMode(data.test_mode || null);
    } catch (e) {
      setTestMode(null);
    }
  }, []);
  useEffect(() => {
    reloadCfg();
    const t = setInterval(reloadCfg, 10000);
    return () => clearInterval(t);
  }, [reloadCfg]);

  const status = state?.status || "idle";
  const isRunning = status === "running" || status === "stopping";

  return (
    <div className="space-y-4">
      {testMode?.active && (
        <div
          className="border-2 border-[#FF3333] bg-red-50 px-4 py-3 flex items-center gap-3"
          data-testid="automation-test-banner"
        >
          <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#FF3333] text-white">
            TEST MODE
          </span>
          <div className="text-sm">
            All outbound mails are redirected to{" "}
            <span className="font-mono font-bold">{testMode.recipient}</span>.
            Real institution recipients will NOT receive anything. Disable in
            Settings → Test mode before going live.
          </div>
        </div>
      )}
      <div className="border border-[#0A0A0A] p-5 bg-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Status</div>
            <div className="mt-1 flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] border ${
                  status === "running"
                    ? "bg-emerald-600 text-white border-emerald-600 animate-pulse"
                    : status === "stopping"
                      ? "bg-amber-500 text-white border-amber-500"
                      : status === "stopped"
                        ? "bg-[#FF3333] text-white border-[#FF3333]"
                        : "bg-white text-[#0A0A0A] border-[#0A0A0A]"
                }`}
                data-testid="automation-status"
              >
                <Activity className="w-3 h-3" /> {status}
              </span>
              {state?.started_at && (
                <span className="font-mono text-[10px] text-[#4B5563]">
                  started {new Date(state.started_at).toLocaleString()} by {state.started_by || "—"}
                </span>
              )}
            </div>
            {isRunning && (
              <div className="mt-3 text-xs">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Current</div>
                <div className="font-bold mt-1" data-testid="automation-current-slug">{state?.current_name || state?.current_slug || "—"}</div>
                <div className="text-[#4B5563]">Step: <span className="font-mono">{state?.current_step || "—"}</span></div>
                {(state?.filter_type || state?.filter_category) && (
                  <div className="text-[#4B5563] mt-1">
                    Filter:{" "}
                    <span className="font-mono" data-testid="automation-active-filter">
                      {state.filter_type || "All"}
                      {state.filter_category ? ` / ${state.filter_category}` : ""}
                    </span>
                  </div>
                )}
                <div className="text-[#4B5563] mt-1">
                  Mode:{" "}
                  <span className="font-mono" data-testid="automation-active-mode">
                    {state?.template_id
                      ? `Fixed template — ${state.template_name || state.template_id}`
                      : "AI-generated"}
                  </span>
                </div>
              </div>
            )}
            {state?.last_error && (
              <div className="mt-3 text-xs text-[#FF3333] border border-[#FF3333] bg-[#FFF5F5] px-2 py-1.5">
                <span className="font-bold">last error:</span> {state.last_error}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isRunning && (
              <BrutalBtn testid="automation-start" variant="success" onClick={handleStart} disabled={busy}>
                <Play className="w-3.5 h-3.5" /> Start automation
              </BrutalBtn>
            )}
            {isRunning && (
              <BrutalBtn testid="automation-stop" variant="danger" onClick={handleStop} disabled={busy || status === "stopping"}>
                <Square className="w-3.5 h-3.5" /> {status === "stopping" ? "Stopping…" : "Stop automation"}
              </BrutalBtn>
            )}
          </div>
        </div>
      </div>

      {!isRunning && (
        <div className="border border-[#0A0A0A] p-4 bg-white" data-testid="automation-filters">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Type</div>
              <select
                value={type_}
                onChange={(e) => { setType(e.target.value); setCategory(""); }}
                data-testid="automation-type"
                className="border border-[#0A0A0A] px-3 py-2 text-xs font-mono min-w-[180px]"
              >
                <option value="">All (Banks + NBFCs)</option>
                <option value="Bank">Bank only</option>
                <option value="NBFC">NBFC only</option>
              </select>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">
                Category {type_ === "Bank" ? "(disabled for Bank)" : "(optional, NBFC sub-types)"}
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="automation-category"
                disabled={type_ === "Bank"}
                className="border border-[#0A0A0A] px-3 py-2 text-xs font-mono min-w-[260px] disabled:bg-[#F3F4F6] disabled:opacity-60"
              >
                <option value="">All categories</option>
                {nbfcCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="ml-auto text-xs">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Scope preview</div>
              <div
                className="border border-[#002FA7] bg-[#EFF4FF] text-[#002FA7] px-3 py-2 font-mono text-sm font-bold"
                data-testid="automation-preview-count"
              >
                {preview?.matches != null
                  ? `${preview.matches.toLocaleString()} institutions`
                  : "—"}
              </div>
              {preview && (
                <div className="font-mono text-[10px] text-[#4B5563] mt-1 leading-relaxed" data-testid="automation-preview-extra">
                  <span className="font-bold text-[#0A0A0A]">{(preview.with_website ?? 0).toLocaleString()}</span> with website ·{" "}
                  <span className="font-bold text-[#0A0A0A]">{(preview.with_on_file_email ?? 0).toLocaleString()}</span> with on-file email ·{" "}
                  <span className="font-bold text-[#FF3333]">{(preview.empty ?? 0).toLocaleString()}</span> no website + no email
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 text-[10px] font-mono text-[#4B5563]">
            Active scope: <span className="font-bold text-[#0A0A0A]">{filterLabel()}</span>. DNC-blocked, already-drafted, and websiteless institutions are automatically excluded inside the runner.
          </div>

          <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">Email mode</div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="automation-mode-ai">
                <input
                  type="radio"
                  name="email-mode"
                  checked={emailMode === "ai"}
                  onChange={() => setEmailMode("ai")}
                  className="accent-[#002FA7]"
                />
                <span className="font-bold">AI-generated</span>
                <span className="text-[#4B5563]">(default — research + pitch per institution)</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="automation-mode-fixed">
                <input
                  type="radio"
                  name="email-mode"
                  checked={emailMode === "fixed"}
                  onChange={() => setEmailMode("fixed")}
                  className="accent-[#002FA7]"
                />
                <span className="font-bold">Fixed template</span>
                <span className="text-[#4B5563]">(no LLM, faster, deterministic)</span>
              </label>
              {emailMode === "fixed" && (
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  data-testid="automation-template"
                  className="border border-[#0A0A0A] px-3 py-2 text-xs font-mono min-w-[320px] ml-2"
                >
                  <option value="">— pick a template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.role})</option>
                  ))}
                </select>
              )}
            </div>
            {emailMode === "fixed" && templates.length === 0 && (
              <div className="mt-2 text-[10px] font-mono text-[#FF3333]">
                No active templates yet. Create one under Settings → Email templates.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Counter testid="automation-scanned" label="Institutions scanned" value={state?.scanned} />
        <Counter testid="automation-harvested" label="New emails harvested" value={state?.harvested_emails} highlight />
        <Counter testid="automation-drafted" label="Drafts auto-created" value={state?.drafted} />
        <Counter testid="automation-sent" label="Emails sent" value={state?.sent_emails} highlight />
        <Counter testid="automation-failed" label="Failed sends" value={state?.failed_sends} danger />
        <Counter testid="automation-skipped-dnc" label="Skipped (DNC)" value={state?.skipped_dnc} />
        <Counter testid="automation-skipped-noemail" label="Skipped (no email)" value={state?.skipped_no_email} />
        <Counter testid="automation-skipped-drafted" label="Skipped (already drafted)" value={state?.skipped_already_drafted} />
        <Counter testid="automation-skipped-verification" label="Skipped (Bouncify)" value={state?.skipped_verification} danger />
      </div>

      <div className="text-xs text-[#4B5563] border border-[#0A0A0A] bg-white px-4 py-3">
        <span className="font-bold">How it works:</span> the runner walks every institution alphabetically (slug order). For each: DNC firewall → site + web harvest → AI research → AI draft → auto-approve → MSG91 fan-out to all on-file emails. Throttled at <code className="font-mono">SALES_SEND_PER_MINUTE</code> (default 50/min). No daily cap. Stop is graceful — the current institution finishes before halt. Status refreshes every 3 seconds.
      </div>

      <DailySummary />
    </div>
  );
};

const DailySummary = () => {
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: res } = await http.get(`/admin/sales/summary/daily?date=${d}`);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Summary failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const sent = data?.sent || {};
  const drafts = data?.drafts || {};

  return (
    <div className="border border-[#0A0A0A] bg-white p-5" data-testid="daily-summary">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Daily summary</div>
          <div className="text-lg font-bold mt-0.5">
            {data?.date || date}
            {data?.is_today && <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 bg-[#002FA7] text-white">live · today</span>}
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Date</div>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="summary-date"
              className="border border-[#0A0A0A] px-3 py-1.5 text-xs font-mono"
            />
          </div>
          <BrutalBtn testid="summary-refresh" variant="ghost" onClick={() => load(date)} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </BrutalBtn>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Counter testid="summary-sent-total"     label="Emails sent"        value={sent.total} highlight />
        <Counter testid="summary-sent-automated" label="By automation"      value={sent.automated} />
        <Counter testid="summary-sent-manual"    label="Manual sends"       value={sent.manual} />
        <Counter testid="summary-sent-followups" label="Follow-ups"         value={sent.followups} />
        <Counter testid="summary-drafts"         label="Drafts created"     value={drafts.created} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Counter testid="summary-delivered" label="Delivered"   value={sent.delivered} />
        <Counter testid="summary-opens"     label="Opens"       value={sent.opens} highlight />
        <Counter testid="summary-clicks"    label="Clicks"      value={sent.clicks} highlight />
        <Counter testid="summary-replied"   label="Replied"     value={sent.replied} highlight />
        <Counter testid="summary-bounced"   label="Bounces"     value={(sent.bounced || 0) + (sent.complained || 0)} danger />
      </div>

      {!!(data?.top_institutions?.length) && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">Top institutions today</div>
          <div className="border border-[#0A0A0A]">
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FA]">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Institution</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Slug</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Sends</th>
                </tr>
              </thead>
              <tbody>
                {data.top_institutions.map((row) => (
                  <tr key={row.slug} className="border-t border-[#E5E7EB]" data-testid={`summary-top-${row.slug}`}>
                    <td className="px-3 py-2 font-bold">{row.name}</td>
                    <td className="px-3 py-2 font-mono text-[#4B5563]">{row.slug}</td>
                    <td className="px-3 py-2 text-right font-bold">{row.sends}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && !data.top_institutions?.length && (
        <div className="text-xs text-[#4B5563] border border-dashed border-[#0A0A0A] px-3 py-4 text-center">
          No outreach activity for {data.date}.
        </div>
      )}
    </div>
  );
};

const Counter = ({ label, value, highlight, danger, testid }) => (
  <div className={`border border-[#0A0A0A] p-3 bg-white`} data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${highlight ? "text-[#002FA7]" : danger ? "text-[#FF3333]" : "text-[#0A0A0A]"}`}>
      {value ?? 0}
    </div>
  </div>
);

/* ============================================================================
 * TAB 5: SETTINGS
 * ========================================================================= */
const SettingsTab = ({ cfg }) => {
  if (!cfg) return <div className="text-sm text-[#4B5563]">Loading…</div>;
  const msg91 = cfg.msg91 || {};
  const bouncify = cfg.bouncify || {};
  const msg91_ok = msg91.configured;
  return (
    <div className="space-y-4 text-sm">
      <div className="border border-[#0A0A0A] p-4 bg-white">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">MSG91 (primary provider)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Setting label="Auth key" value={msg91.configured ? "✓ configured" : "✗ NOT configured"} danger={!msg91_ok} />
          <Setting label="Domain" value={msg91.domain || "—"} danger={!msg91.domain} />
          <Setting label="From email" value={msg91.from_email || "—"} />
          <Setting label="From name" value={msg91.from_name || "—"} />
          <Setting label="Template ID" value={msg91.template_id_set ? "✓ configured" : "✗ NOT set"} danger={!msg91.template_id_set} />
          <Setting label="Reply-to" value={cfg.reply_to || "—"} />
        </div>
        {!msg91_ok && (
          <p className="text-xs text-[#FF3333] mt-3">
            {msg91.missing}. Create a template in MSG91 dashboard (HTML body with {`{{subject}}`} + {`{{body_html}}`} variables), copy the template ID into <code className="font-mono">MSG91_TEMPLATE_ID</code> in <code className="font-mono">/app/backend/.env</code>, then run <code className="font-mono">sudo supervisorctl restart backend</code>.
          </p>
        )}
      </div>
      <div className="border border-[#0A0A0A] p-4 bg-white">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">Bouncify (pre-send verification)</div>
        <Setting
          label="API key"
          value={bouncify.configured ? "✓ configured — invalid addresses will be skipped before send" : "✗ NOT configured — all on-file emails will be sent without verification"}
          danger={!bouncify.configured}
        />
      </div>

      <TestModeCard cfg={cfg} />
      <div className="border border-[#0A0A0A] p-4 bg-white">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">Webhook (open / click / bounce — Resend legacy)</div>
        <Setting label="Webhook token" value={cfg.webhook_token_configured ? "✓ configured" : "✗ NOT configured"} danger={!cfg.webhook_token_configured} />
        <div className="mt-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Webhook URL</div>
          <code className="block border border-[#0A0A0A] p-2 font-mono text-[11px] break-all bg-[#F8F9FA]" data-testid="settings-webhook-url">{cfg.webhook_url || "(set PUBLIC_SITE_URL in backend/.env)"}</code>
        </div>
      </div>
      <div className="border border-[#0A0A0A] p-4 bg-white">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">PrivateCourt links (canonical)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Setting label="Presentation" value={cfg.presentation_url} />
          <Setting label="Signup" value={cfg.signup_url} />
        </div>
      </div>

      <TemplateLibrary />
    </div>
  );
};

/* --- Email Templates library — CRUD for fixed (non-AI) outreach copy --- */
const TemplateLibrary = () => {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | {id?: string, ...form}
  const [previewing, setPreviewing] = useState(null); // {subject, html} | null

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/sales/templates");
      setItems(data.items || []);
      setPlaceholders(data.placeholders || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Load failed"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const newTemplate = () => setEditing({
    name: "", role: "any", subject: "", body_md: "", is_active: true,
  });

  const editTemplate = (t) => setEditing({
    id: t.id, name: t.name, role: t.role || "any", subject: t.subject,
    body_md: t.body_md, is_active: !!t.is_active,
  });

  const save = async () => {
    if (!editing.name?.trim() || !editing.subject?.trim() || !editing.body_md?.trim()) {
      toast.error("Name, subject and body are all required");
      return;
    }
    setBusy(true);
    const t = toast.loading(editing.id ? "Saving…" : "Creating…");
    try {
      if (editing.id) {
        await http.patch(`/admin/sales/templates/${editing.id}`, {
          name: editing.name, role: editing.role, subject: editing.subject,
          body_md: editing.body_md, is_active: editing.is_active,
        });
      } else {
        await http.post("/admin/sales/templates", {
          name: editing.name, role: editing.role, subject: editing.subject,
          body_md: editing.body_md, is_active: editing.is_active,
        });
      }
      toast.success("Saved", { id: t });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Save failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/sales/templates/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Delete failed"));
    }
  };

  const preview = async (id) => {
    setBusy(true);
    try {
      const { data } = await http.get(`/admin/sales/templates/${id}/preview`);
      setPreviewing(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#0A0A0A] p-4 bg-white" data-testid="template-library">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
          Email templates ({items.length}) — fixed copy (no AI)
        </div>
        <BrutalBtn testid="template-new" variant="ghost" onClick={newTemplate}>
          + New template
        </BrutalBtn>
      </div>

      <div className="border border-[#0A0A0A]">
        <table className="w-full text-xs">
          <thead className="bg-[#F8F9FA]">
            <tr>
              <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Name</th>
              <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Role</th>
              <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Subject</th>
              <th className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Active</th>
              <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-t border-[#E5E7EB]" data-testid={`template-row-${t.id}`}>
                <td className="px-3 py-2 font-bold">{t.name}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{t.role}</td>
                <td className="px-3 py-2 text-[#4B5563]">{(t.subject || "").slice(0, 80)}</td>
                <td className="px-3 py-2 text-center">
                  {t.is_active ? <span className="text-emerald-600 font-bold">●</span> : <span className="text-[#9CA3AF]">○</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button className="text-[#002FA7] hover:underline font-bold mr-3" onClick={() => preview(t.id)} data-testid={`template-preview-${t.id}`}>Preview</button>
                  <button className="text-[#0A0A0A] hover:underline font-bold mr-3" onClick={() => editTemplate(t)} data-testid={`template-edit-${t.id}`}>Edit</button>
                  <button className="text-[#FF3333] hover:underline font-bold" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="5" className="px-3 py-6 text-center text-[#4B5563]">No templates yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="template-editor">
          <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="border-b border-[#0A0A0A] px-5 py-3 flex items-center justify-between bg-[#F8F9FA]">
              <div className="font-bold">{editing.id ? "Edit template" : "New template"}</div>
              <button onClick={() => setEditing(null)} className="text-[#4B5563] hover:text-black">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Name</div>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="template-form-name"
                  className="w-full border border-[#0A0A0A] px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Role</div>
                  <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                    data-testid="template-form-role"
                    className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono">
                    <option value="any">Any</option>
                    <option value="recovery">Recovery</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Active</div>
                  <select value={editing.is_active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "1" })}
                    data-testid="template-form-active"
                    className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono">
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Subject</div>
                <input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  data-testid="template-form-subject"
                  className="w-full border border-[#0A0A0A] px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Body (Markdown)</div>
                  <div className="font-mono text-[9px] text-[#4B5563]">Placeholders: {placeholders.map((p) => `{${p}}`).join(", ")}</div>
                </div>
                <textarea value={editing.body_md} onChange={(e) => setEditing({ ...editing, body_md: e.target.value })}
                  data-testid="template-form-body"
                  rows={16}
                  className="w-full border border-[#0A0A0A] px-3 py-2 text-xs font-mono leading-relaxed" />
              </div>
            </div>
            <div className="border-t border-[#0A0A0A] px-5 py-3 flex justify-end gap-2 bg-[#F8F9FA]">
              <BrutalBtn testid="template-cancel" variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</BrutalBtn>
              <BrutalBtn testid="template-save" variant="primary" onClick={save} disabled={busy}>{editing.id ? "Save changes" : "Create"}</BrutalBtn>
            </div>
          </div>
        </div>
      )}

      {previewing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="template-preview-modal">
          <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="border-b border-[#0A0A0A] px-5 py-3 flex items-center justify-between bg-[#F8F9FA]">
              <div className="text-xs">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Preview · rendered against</div>
                <div className="font-bold">{previewing.rendered_for?.name || "—"}</div>
              </div>
              <button onClick={() => setPreviewing(null)} className="text-[#4B5563] hover:text-black">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Subject</div>
                <div className="border border-[#0A0A0A] px-3 py-2 text-sm font-bold">{previewing.subject}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Rendered HTML</div>
                <iframe
                  title="Template preview"
                  srcDoc={previewing.html}
                  className="w-full h-[60vh] border border-[#0A0A0A] bg-white"
                  data-testid="template-preview-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
/* --- Test-mode card: redirect every outbound mail to a single inbox --- */
const TestModeCard = ({ cfg }) => {
  const initial = cfg?.test_mode?.recipient || "";
  const [recipient, setRecipient] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(cfg?.test_mode?.active || false);

  useEffect(() => {
    setRecipient(cfg?.test_mode?.recipient || "");
    setActive(!!cfg?.test_mode?.active);
  }, [cfg]);

  const save = async (rcpt) => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/sales/test-mode", { recipient: rcpt });
      setActive(!!data.active);
      setRecipient(data.recipient || "");
      toast.success(data.active ? `Test mode ON — sending to ${data.recipient}` : "Test mode OFF — normal routing");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Update failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`border-2 p-4 ${active ? "border-[#FF3333] bg-red-50" : "border-[#0A0A0A] bg-white"}`}
      data-testid="test-mode-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Test mode (redirect all sends)</div>
          {active ? (
            <div className="text-sm">
              <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#FF3333] text-white">
                ON
              </span>{" "}
              All outbound mails are being redirected to{" "}
              <span className="font-mono font-bold" data-testid="test-mode-recipient">{recipient}</span>.
              Subject is prefixed with the intended target.
            </div>
          ) : (
            <div className="text-sm text-[#4B5563]">
              OFF. Mails go to the real institution recipients.
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">Redirect target</div>
          <input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="av211988@gmail.com"
            data-testid="test-mode-input"
            className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono"
          />
        </div>
        <BrutalBtn testid="test-mode-enable" variant="primary" onClick={() => save(recipient)} disabled={busy || !recipient}>
          {active ? "Update target" : "Enable test mode"}
        </BrutalBtn>
        {active && (
          <BrutalBtn testid="test-mode-disable" variant="danger" onClick={() => save("")} disabled={busy}>
            Disable
          </BrutalBtn>
        )}
      </div>
    </div>
  );
};

const Setting = ({ label, value, danger }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</div>
    <div className={`border border-[#0A0A0A] px-3 py-2 font-mono text-xs ${danger ? "bg-red-50 text-[#FF3333]" : "bg-[#F8F9FA]"}`}>{value}</div>
  </div>
);

/* ============================================================================
 * MAIN
 * ========================================================================= */
export const SalesPanel = () => {
  const [tab, setTab] = useState("leads");
  const [cfg, setCfg] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { http.get("/admin/sales/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);

  const tabs = useMemo(() => [
    { k: "leads", label: "Lead pool", icon: Users },
    { k: "queue", label: "Approval queue", icon: ListChecks },
    { k: "sent", label: "Sent & engagement", icon: Inbox },
    { k: "followups", label: "Follow-ups", icon: Clock },
    { k: "automation", label: "Automation", icon: Activity },
    { k: "settings", label: "Settings", icon: SettingsIcon },
  ], []);

  return (
    <div className="border border-[#0A0A0A] p-6 bg-[#F8F9FA]" data-testid="sales-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">PrivateCourt sales outreach</div>
            <div className="font-bold text-sm mt-0.5">AI-researched · human-approved · DNC-firewalled · auto-follow-ups</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {cfg && (
            <>
              <span className={`${PILL} bg-white`} data-testid="sales-cfg-from">From: {cfg.from_email}</span>
              <span
                className={`${PILL} ${cfg.resend_key_configured ? "bg-emerald-600 text-white border-emerald-600" : "bg-[#FF3333] text-white border-[#FF3333]"}`}
                data-testid="sales-cfg-status"
              >
                {cfg.resend_key_configured ? "Resend ✓" : "Resend key missing"}
              </span>
            </>
          )}
        </div>
      </div>

      {cfg && !cfg.resend_key_configured && (
        <div className="mb-4 border border-[#FF3333] bg-[#FFF5F5] px-3 py-2 text-xs flex items-start gap-2" data-testid="sales-key-warning">
          <AlertTriangle className="w-3.5 h-3.5 text-[#FF3333] mt-0.5 shrink-0" />
          <div>
            <span className="font-bold">RESEND_API_KEY not configured.</span> You can draft and approve, but sending will fail until you set the key in <code className="font-mono">backend/.env</code>. Go to <span className="underline cursor-pointer" onClick={() => setTab("settings")}>Settings</span>.
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`sales-tab-${t.k}`}
            className={`${PILL} flex items-center gap-2 ${tab === t.k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white"}`}
          >
            <t.icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "leads" && <LeadPoolTab onBulkDrafted={() => { setRefreshKey((k) => k + 1); setTab("queue"); }} />}
      {tab === "queue" && <QueueTab refreshKey={refreshKey} />}
      {tab === "sent" && <SentTab />}
      {tab === "followups" && <FollowupsTab />}
      {tab === "automation" && <AutomationTab />}
      {tab === "settings" && <SettingsTab cfg={cfg} />}
    </div>
  );
};

export default SalesPanel;
